import { makeObservable, observable, action, runInAction } from 'mobx';
import { StripesBaseStore, UIElementExtended } from './store-stripes-base';
import { UIElement, Universe } from '@smart-tv/typings/content';
import getProviderCollectionContentGenres from '@smart-tv/utils/kmm/getProviderCollectionContentGenres';
import getProviderCollectionUniverses from '@smart-tv/utils/kmm/getProviderCollectionUniverses';
import { UIElementContentSource } from '@smart-tv/hooks/use-ui-element-content/use-ui-element-content';
import getProviderCollectionContentList from '@smart-tv/utils/kmm/getProviderCollectionContentList';
import { PageType } from '../_utils/types';
import { getAreaForPageType } from '@smart-tv/utils/kmm/helpers';
import { getUiElementContent } from '@smart-tv/utils/kmm/getUiElementContent';

interface ProviderParams {
  providerId: number;
  universeId?: number;
  favoritesListId?: number;
}

class StripesProviderStore extends StripesBaseStore {
  params: ProviderParams | null = null;
  heroUiElements: UIElementExtended | null = null;
  responseNavigationContent: Universe[] = [];
  selectedUniverseId: number | undefined = undefined;

  private genreFetchGeneration = 0;

  constructor() {
    super();
    makeObservable(this, {
      params: observable,
      heroUiElements: observable,
      responseNavigationContent: observable,
      selectedUniverseId: observable,
      setParams: action,
      setHeroUiElements: action,
      setResponseNavigationContent: action,
      setSelectedUniverseId: action,

      fetchNavigationContent: action,
      fetchGenres: action,
      fetchUiElements: action,
      resetStore: action,
      refresh: action
    });

    // Bind methods to ensure correct 'this' context
    this.enrichProviderUiElement = this.enrichProviderUiElement.bind(this);
  }

  resetStore = () => {
    this.stopUiElementsSubscription();
    this.resetUiElements();
    this.params = null;
    this.heroUiElements = null;
    this.responseNavigationContent = [];
    this.selectedUniverseId = undefined;
    this.setLoading(true);
    this.setActiveUIElement(null);
    // Invalidates any in-flight fetchGenres() so it can't write stale data after a reset.
    this.genreFetchGeneration++;
  };

  setParams = (params: ProviderParams) => {
    this.params = params;
  };

  setHeroUiElements = (hero: UIElementExtended | null) => {
    this.heroUiElements = hero;
  };

  setResponseNavigationContent = (content: Universe[]) => {
    this.responseNavigationContent = content;
  };

  setSelectedUniverseId = (id: number | undefined) => {
    this.selectedUniverseId = id;
  };

  private splitHeroAndUiElements(uiElements: UIElementExtended[]): {
    heroUiElement: UIElementExtended | null;
    remainingUiElements: UIElementExtended[];
  } {
    const heroIndex = uiElements.findIndex((uiElement) => uiElement.hasItems);

    if (heroIndex < 0) {
      return {
        heroUiElement: null,
        remainingUiElements: []
      };
    }

    return {
      heroUiElement: uiElements[heroIndex],
      remainingUiElements: uiElements.filter(
        (uiElement, index) => index !== heroIndex && uiElement.hasItems
      )
    };
  }

  protected async enrichProviderUiElement(uiElement: UIElement): Promise<UIElementExtended> {
    let stripeHasItems = false;
    const contentSource = uiElement.contentSource;
    const providerContentProps = uiElement.providerContentProps;
    const style = uiElement.style;

    try {
      if (contentSource === UIElementContentSource.Provider && providerContentProps && style) {
        providerContentProps.itemLimit = 9;
        const content = await getProviderCollectionContentList(providerContentProps, style);
        stripeHasItems = content.items.length > 0;

        return {
          ...uiElement,
          hasItems: stripeHasItems,
          content
        };
      } else if (contentSource === UIElementContentSource.UIelements) {
        const response = await getUiElementContent(
          Number(uiElement.id),
          this.params?.favoritesListId,
          getAreaForPageType(PageType.ProviderUniverse, undefined, this.params?.providerId)
        );
        stripeHasItems = response?.items?.length > 0;

        return {
          ...uiElement,
          hasItems: stripeHasItems,
          content: response
        };
      }

      return {
        ...uiElement,
        hasItems: stripeHasItems
      };
    } catch (error) {
      console.error('Error enriching UI element:', error);
      return {
        ...uiElement,
        hasItems: false
      };
    }
  }

  async fetchNavigationContent(): Promise<void> {
    if (!this.params?.providerId) return;

    const response = await getProviderCollectionUniverses(this.params.providerId);
    runInAction(() => {
      this.setResponseNavigationContent(response);
    });
  }

  async fetchGenres(): Promise<boolean> {
    if (!this.params?.providerId || !this.params?.universeId) return false;

    const generation = ++this.genreFetchGeneration;

    const response = await getProviderCollectionContentGenres(
      this.params.providerId,
      this.params.universeId
    );

    const enrichedUiElements = await Promise.all(response.map(this.enrichProviderUiElement));

    if (generation !== this.genreFetchGeneration) return false;

    let applied = false;

    runInAction(() => {
      const uiElementsWithItems = enrichedUiElements.filter((uiElement) => uiElement.hasItems);

      if (!this.heroUiElements?.hasItems) {
        const { heroUiElement, remainingUiElements } =
          this.splitHeroAndUiElements(uiElementsWithItems);
        if (!heroUiElement) return;
        this.setHeroUiElements(heroUiElement);
        this.setUiElements(remainingUiElements);
        applied = true;
        return;
      }

      this.setUiElements(uiElementsWithItems);
      applied = true;
    });

    return applied;
  }

  async fetchUiElements(): Promise<void> {
    if (!this.params?.providerId) return;

    const { favoritesListId, providerId } = this.params;
    const area = getAreaForPageType(PageType.ProviderUniverse, undefined, providerId);

    await this.subscribeUiElementsBase(favoritesListId, area, async (uiElements, isStale) => {
      const enrichedUiElements = await Promise.all(uiElements.map(this.enrichProviderUiElement));
      if (isStale()) return;

      runInAction(() => {
        const { heroUiElement, remainingUiElements } =
          this.splitHeroAndUiElements(enrichedUiElements);

        this.setHeroUiElements(heroUiElement);
        this.setUiElements(remainingUiElements);
      });
    });
  }

  refresh = async (): Promise<void> => {
    this.resetUiElements();
    await Promise.all([this.fetchUiElements(), this.fetchNavigationContent()]);
  };
}

export default StripesProviderStore;
